import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Grid, IconButton } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupCategories from './GroupCategories';
import PrimaryGroups from './PrimaryGroups';
import SubGroups from './SubGroups';
import Ledgers from './Ledgers';
import GSTSettlement from '../Taxes/GSTSettlement';
import SalaryList from '../Salary/SalaryList';
import CostCentreManagement from '../CostCentre/CostCentreManagement';
import StatutoryCompliance from '../Taxes/StatutoryCompliance';
import ExpenseSettlement from './ExpenseSettlement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coa-tabpanel-${index}`}
      aria-labelledby={`coa-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `coa-tab-${index}`,
    'aria-controls': `coa-tabpanel-${index}`,
  };
}

const ChartsOfAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(0);

  // Check if we need to switch to Taxes tab based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'taxes') {
      setValue(4); // Set to Taxes tab
    }
  }, [searchParams]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <>
      {/* Sticky Tabs Section - Outside main container */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Charts of Account tabs"
          sx={{
            px: 1,
            '& .MuiTabs-indicator': {
              backgroundColor: '#059669',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#545454',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#059669',
                fontWeight: 600,
              },
              '&:hover': {
                color: '#059669',
                backgroundColor: 'rgba(5, 150, 105, 0.04)',
              },
            },
          }}
        >
          <Tab label="Group Categories" {...a11yProps(0)} />
          <Tab label="Primary Groups" {...a11yProps(1)} />
          <Tab label="Sub Groups" {...a11yProps(2)} />
          <Tab label="Ledgers" {...a11yProps(3)} />
          <Tab label="Tax Settlement" {...a11yProps(4)} />
          <Tab label="Expense Settlement" {...a11yProps(5)} />
          <Tab label="Salary Management" {...a11yProps(6)} />
          <Tab label="Statutory Compliance" {...a11yProps(7)} />
          <Tab label="Cost Centres" {...a11yProps(8)} />
        </Tabs>
      </Box>

      {/* Scrollable Content Section */}
      <Box sx={{ px: 1 }}>
        <Box sx={{ bgcolor: '#fff', minHeight: '70vh' }}>
          <TabPanel value={value} index={0}>
            <GroupCategories />
          </TabPanel>

          <TabPanel value={value} index={1}>
            <PrimaryGroups />
          </TabPanel>

          <TabPanel value={value} index={2}>
            <SubGroups />
          </TabPanel>

          <TabPanel value={value} index={3}>
            <Ledgers />
          </TabPanel>

          <TabPanel value={value} index={4}>
            <GSTSettlement />
          </TabPanel>

          <TabPanel value={value} index={5}>
            <ExpenseSettlement />
          </TabPanel>

          <TabPanel value={value} index={6}>
            <SalaryList />
          </TabPanel>

          <TabPanel value={value} index={7}>
            <StatutoryCompliance />
          </TabPanel>

          <TabPanel value={value} index={8}>
            <CostCentreManagement />
          </TabPanel>
        </Box>
      </Box>
    </>
  );
};

export default ChartsOfAccount;
